/**
 * Обновляет структуру табов, обновляет только измененные данные в list и сортирует.
 *
 * @param {Tabs} storeData - текущая структура табов.
 * @param {Tabs} serverData - данные с сервера для обновления.
 * @returns {Tabs} - обновленная структура табов.
 */

import { List, Tabs } from '../types/tasksInformationSchema';
import { deepEqual } from 'shared/helpers';

export function getUpdatedTabs(store: Tabs, serverData: Tabs): Tabs {
    if (deepEqual(store, serverData)) return store;
    // Создаю глубокую копию store
    const newStore: Tabs = {
        board: { ...store.board },
        list: { ...store.list },
    };

    // Копирую вложенные массивы для board
    Object.keys(newStore.board).forEach((key) => {
        newStore.board[key] = [...newStore.board[key]];
    });

    // Убрал мутацию стора версия mergeArrays
    const mergeArrays = (storeArr: string[], serverArr: string[]) => {
        const existing = new Set(storeArr);
        return [...storeArr, ...serverArr.filter((id) => !existing.has(id))];
    };

    // Поиск newBoardKey и uid без мутаций
    const { newBoardKey, uid } = Object.entries(serverData.board).reduce(
        (acc, [key, ids]) => {
            if (ids.length > 0 && !acc.newBoardKey) {
                return { newBoardKey: key, uid: ids[0] };
            }
            return acc;
        },
        { newBoardKey: null as string | null, uid: null as string | null }
    );

    // Иммутабельно обновление доски
    if (newBoardKey && uid) {
        // Создаем новый board
        const updatedBoard = { ...newStore.board };

        // Удаляем uid из всех категорий кроме новой
        Object.keys(updatedBoard).forEach((tab) => {
            if (tab !== newBoardKey) {
                updatedBoard[tab] = updatedBoard[tab].filter((id) => id !== uid);
            }
        });

        // Мерджим данные для новой категории
        updatedBoard[newBoardKey] = mergeArrays(
            updatedBoard[newBoardKey] || [],
            serverData.board[newBoardKey]
        );

        newStore.board = updatedBoard;
    }

    // Иммутабельная обработка списка
    const taskIDsToAdd = new Set<string>();
    let newList = { ...newStore.list };

    // Первый проход: добавление новых задач
    newList = Object.entries(serverData.list).reduce(
        (acc, [date, serverTasks]) => {
            if (serverTasks.length === 0) return acc;

            const merged = mergeArrays(acc[date] || [], serverTasks);
            serverTasks.forEach((id) => taskIDsToAdd.add(id));
            return { ...acc, [date]: merged };
        },
        { ...newList }
    );

    // Второй проход: удаление задач из других дат
    newList = Object.entries(newList).reduce((acc, [date, tasks]) => {
        const filtered = tasks.filter(
            (taskID) => !taskIDsToAdd.has(taskID) || serverData.list[date]?.includes(taskID)
        );
        return filtered.length > 0 ? { ...acc, [date]: filtered } : acc;
    }, {} as List);

    // Удаление пустых дат иммутабельно
    const filteredList = Object.entries(newList)
        .filter(([_, tasks]) => tasks.length > 0)
        .reduce(
            (acc, [date, tasks]) => ({
                ...acc,
                [date]: tasks,
            }),
            {} as List
        );

    // Сортировка
    const sortedEntries = Object.entries(filteredList).sort(([keyA], [keyB]) => {
        const getPriority = (key: string) => {
            if (key === 'completed') return 1;
            if (key === 'no_term') return 2;
            return 3;
        };

        const parseDate = (key: string) => {
            const [y, m, d] = key.split('-').map(Number);
            return new Date(y, m - 1, d).getTime();
        };

        const priorityA = getPriority(keyA);
        const priorityB = getPriority(keyB);

        if (priorityA !== priorityB) return priorityA - priorityB;
        if (priorityA === 3) return parseDate(keyA) - parseDate(keyB);
        return 0;
    });

    newStore.list = Object.fromEntries(sortedEntries);

    return newStore;
}
